import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { TopMenu } from "./TopMenu";
import { SSMClient, GetParameterCommand } from "@aws-sdk/client-ssm";

const TestCall = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [subscriberPhone, setSubscriberPhone] = useState<string | null>(null);
  const [callInitiated, setCallInitiated] = useState(false);
  const [onboardingStage, setOnboardingStage] = useState<string | null>(null);
  const [purchasedNumber, setPurchasedNumber] = useState<string | null>(null);
  const [ssmParameterName, setSsmParameterName] = useState<string | null>(null);

  useEffect(() => {
    const fetchSubscriberInfo = async () => {
      try {
        const subscriber_id = localStorage.getItem('subscriber_id');
        if (!subscriber_id) {
          console.log('No subscriber_id found');
          toast({
            title: "Error",
            description: "No subscriber information found",
            variant: "destructive",
          });
          navigate('/register');
          return;
        }

        console.log('Fetching subscriber info for subscriber_id:', subscriber_id);
        const { data: subscriberData, error: subscriberError } = await supabase
          .from('subscribers')
          .select('phone, onboarding_stage, oneinbox_purchased_number, oneinbox_ssm_parameter_name')
          .eq('subscriber_id', subscriber_id)
          .single();

        if (subscriberError) {
          console.error('Error fetching subscriber:', subscriberError);
          toast({
            title: "Error",
            description: "Failed to fetch subscriber information",
            variant: "destructive",
          });
          return;
        }

        if (!subscriberData) {
          console.log('No subscriber found for subscriber_id:', subscriber_id);
          toast({
            title: "Error",
            description: "No subscriber information found",
            variant: "destructive",
          });
          return;
        }

        setSubscriberPhone(subscriberData.phone);
        setOnboardingStage(subscriberData.onboarding_stage);
        setPurchasedNumber(subscriberData.oneinbox_purchased_number);
        setSsmParameterName(subscriberData.oneinbox_ssm_parameter_name);
        console.log('Subscriber info fetched:', subscriberData);
      } catch (error) {
        console.error('Error in fetchSubscriberInfo:', error);
        toast({
          title: "Error",
          description: "Failed to fetch user information",
          variant: "destructive",
        });
      }
    };

    fetchSubscriberInfo();
  }, [navigate, toast]);
//
  const getSSMParameter = async (subscriberId) => {
    let rawDataFromInvoke;
    try {
      const { data, error: invokeError } = await supabase.functions.invoke('get-ssm-parameter', {
        body: JSON.stringify({
          subscriber_id: subscriberId
        }),
      });

      rawDataFromInvoke = data;

      console.log('Raw data returned from invoke:', rawDataFromInvoke);
      console.log('Type of data returned from invoke:', typeof rawDataFromInvoke); // Crucial log!

      // Handle invocation-level errors (network, function 5xx status, etc.)
      if (invokeError) {
        console.error('Error calling Edge Function (Invoke Error):', invokeError);
        throw new Error(`Failed to invoke Edge Function: ${invokeError.message || 'Unknown invoke error'}`);
      }

      // Check if the raw data is fundamentally missing
      if (rawDataFromInvoke === null || rawDataFromInvoke === undefined) {
        console.error('Function invocation returned null or undefined data:', rawDataFromInvoke);
        throw new Error('Edge Function returned no data');
      }

      let functionResponseData; // This will hold the object { value: "...", success: ... }

      // CASE 1: Supabase client returned a string that needs parsing
      if (typeof rawDataFromInvoke === 'string') {
        console.log('Raw data is a string, attempting outer parse...');
        try {
          functionResponseData = JSON.parse(rawDataFromInvoke);
          // Ensure the parsed result is an object
          if (typeof functionResponseData !== 'object' || functionResponseData === null) {
              console.error('Parsed string did not result in an object:', functionResponseData);
              throw new Error('Returned string is not valid JSON representing an object.');
          }
        } catch (e) {
          console.error('Failed to parse the outer JSON string returned by Supabase client:', e);
          console.error('String content that failed parsing:', rawDataFromInvoke);
          throw new Error('Invalid JSON string returned by Edge Function.');
        }
      }
      // CASE 2: Supabase client already parsed it into an object (expected)
      else if (typeof rawDataFromInvoke === 'object') {
        console.log('Raw data is already an object.');
        functionResponseData = rawDataFromInvoke;
      }
      // CASE 3: Unexpected type
      else {
        console.error('Expected object or string from invoke, received:', typeof rawDataFromInvoke, rawDataFromInvoke);
        throw new Error(`Unexpected data type returned by Edge Function: ${typeof rawDataFromInvoke}`);
      }

      // --- Now functionResponseData should reliably be the outer object ---
      console.log('Successfully processed outer object:', functionResponseData);

      // Validate the structure: must have 'value' property as a string
      if (typeof functionResponseData.value !== 'string') {
        console.error('Processed data object lacks a string "value" property:', functionResponseData);
        console.log('Success status from function (if available):', functionResponseData.success);
        throw new Error('Parameter value property is missing or not a string in the response object');
      }

      console.log('Inner "value" string to be parsed:', functionResponseData.value);

      // Parse the INNER JSON string (which contains the actual credentials)
      let parsedCredentials;
      try {
        parsedCredentials = JSON.parse(functionResponseData.value);
      } catch (parseError) {
        console.error('Error parsing inner JSON string (credentials):', parseError);
        console.error('String that failed parsing:', functionResponseData.value); // Log the problematic inner string
        throw new Error('Failed to parse the parameter value string containing credentials');
      }

      // Validate the parsed credentials format
      if (typeof parsedCredentials !== 'object' || parsedCredentials === null) {
        console.error('Parsed credentials value is not an object:', parsedCredentials);
        throw new Error('Parsed parameter credentials are not the expected object format');
      }

      // Check the success flag from the function response if it exists and matters
      if (functionResponseData.success === false) {
        console.error('Edge function indicated failure:', functionResponseData);
        // Use error message from function if provided
        throw new Error(functionResponseData.error || 'Edge function reported an execution error');
      }

      console.log('Successfully parsed credentials:', parsedCredentials);
      return parsedCredentials;
    } catch (error) {
      console.error('Error fetching SSM parameter:', error);
      throw new Error('Failed to fetch API credentials');
    }
  };

  const handleSkip = () => {
    navigate("/call-history");
  };

  const handleMakeCall = async () => {
    setIsLoading(true);
    try {
      if (!ssmParameterName) {
        throw new Error('SSM parameter name not found');
      }

      if (!subscriberPhone) {
        throw new Error('Subscriber phone number not available');
      }

      if (!purchasedNumber) {
        throw new Error('No purchased number found. Please complete the time preference setup first.');
      }

      const subscriberId = localStorage.getItem('subscriber_id');
      console.log('subscriber_id:', subscriberId);
    
      if (!subscriberId) throw new Error('Subscriber ID not found');

      const apiCredentials = await getSSMParameter(subscriberId);
      
      if (!apiCredentials) {
        throw new Error('Failed to retrieve API credentials');
      }

      console.log('Making outbound call with:', {
        from_number: purchasedNumber,
        to_number: subscriberPhone
      });
      
      const callResponse = await fetch('https://dev-api.oneinbox.ai/voice/api/call/outbound', {
        method: 'POST',
        headers: {
          'api-key': apiCredentials.apiKey,
          'api-secret': apiCredentials.apiSecret,
          'Content-Type': 'application/json', 
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          from_number: purchasedNumber,
          to_number: subscriberPhone
        })
      });

      if (!callResponse.ok) {
        const errorData = await callResponse.json();
        throw new Error(errorData.message || 'Failed to initiate call');
      }

      const callData = await callResponse.json();
      console.log('Call initiated:', callData);

      toast({
        title: "Success",
        description: "Test call initiated successfully!",
      });

      setCallInitiated(true);

    } catch (error: any) {
      console.error('Error in handleMakeCall:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to make test call",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <TopMenu
        onboardingStage={onboardingStage}
        onTestCallClick={handleMakeCall}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <Card className="max-w-2xl mx-auto p-8 bg-white rounded-lg shadow-sm">
          <div className="flex flex-col items-center space-y-6 text-center">
            <h1 className="text-2xl font-semibold">Try a test call</h1>
            
            <img 
              src="/lovable-uploads/b5942870-a133-456e-8a1e-1bf5aa4c5c9a.png" 
              alt="Phone call illustration" 
              className="w-64 h-64 object-contain"
            />

            <div className="flex gap-4 mt-6">
              <Button 
                variant="default" 
                onClick={callInitiated ? () => navigate("/call-history") : handleMakeCall}
                disabled={isLoading}
              >
                {isLoading ? "Processing..." : callInitiated ? "I got the call" : "Make a test call"}
              </Button>
              <Button 
                variant="secondary" 
                onClick={handleSkip}
                disabled={isLoading}
              >
                Skip
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default TestCall;
